"use client";
import React, {
  createContext,
  Dispatch,
  SetStateAction,
  useContext,
  useState,
  useEffect,
  useMemo,
} from "react";
import useSession from "@/utils/use-session";
import { useSession as useSalesforceSession } from "next-auth/react";
import browserPersistence from "@/utils/session-persistance";
import { useRouter } from "next/navigation";
import { CUSTOMER_DETAILS_KEY, customerOperations } from "@/utils/form-data-manager";
import getSalesforceUserInfo from "@/utils/salesforce/cpq-quote-api/get-user-info";
import { access } from "fs";

const sessionStorage = new browserPersistence();

export interface TypeCustomerDetails {
  id: string;
  firstName: string;
  lastName: string;
  gender: string;
  dob: string;
  email: string;
}

interface AuthContextType {
  isLoggedIn: boolean;
    isLoading: boolean;
  customerDetails: TypeCustomerDetails;
  setCustomerDetails: Dispatch<SetStateAction<TypeCustomerDetails>>;
  setIsLoggedIn: Dispatch<SetStateAction<boolean>>;
  cpqSessionStatus: boolean;
  setCpqSessionStatus: Dispatch<SetStateAction<boolean>>;
  activeSession: "magento" | "salesforce" | null;
}

const defaultCustomerDetails = {
  id: "",
  firstName: "",
  lastName: "",
  gender: "",
  dob: "",
  email: "",
};

const AuthContext = createContext<AuthContextType>({
  isLoggedIn: false,
    isLoading: true,
  customerDetails: defaultCustomerDetails,
  setIsLoggedIn: () => {},
  setCustomerDetails: () => {},
  cpqSessionStatus: false,
  setCpqSessionStatus: () => {},
  activeSession: null,
});

interface AuthContextProviderProps {
  children: React.ReactNode;
}

export const AuthContextProvider: React.FC<AuthContextProviderProps> = ({
  children,
}) => {
  const [isLoggedIn, setIsLoggedIn] = useState<boolean>(false);
  const [customerDetails, setCustomerDetails] = useState<TypeCustomerDetails>(
    defaultCustomerDetails
  );
  const [cpqSessionStatus, setCpqSessionStatus] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true); 

  const [activeSession, setActiveSession] = useState<
    "magento" | "salesforce" | null
  >(null);
  const { session, getCpqLogin, getCustomerDetails } = useSession();
  const { data: salesforceSession, status } = useSalesforceSession();
  
  const router = useRouter();

  useEffect(() => {
    const syncSessionWithAuth = async () => {
            setIsLoading(true);
      if (salesforceSession && salesforceSession.access_token) {
        setIsLoggedIn(true);
        setActiveSession("salesforce");
        const accessToken = salesforceSession?.access_token;
        let salesRepId = "";
        if (accessToken) {
            try {
              const userDetailsResult = await getSalesforceUserInfo(accessToken); 
              if (userDetailsResult.data) {
                salesRepId = userDetailsResult.data?.data?.id || "";
              } else if (userDetailsResult.error) {
                console.error("Error:", userDetailsResult.error);
              }
            } catch (error) {
              console.error("Failed to fetch Salesforce user details:", error);
            }
          }
        const customerData = {
          id: salesRepId ?? "",
          firstName: salesforceSession.user?.name ?? "",
          lastName: "",
          gender: "",
          dob: "",
          email: salesforceSession.user?.email ?? "",
        };

        setCustomerDetails(customerData);
        sessionStorage.setItem(CUSTOMER_DETAILS_KEY, customerData);
        const response = await getCpqLogin();
        if (!response.error) {
          setCpqSessionStatus(true);
        } else {
          setCpqSessionStatus(false);
        }
      } else if (session.isLoggedIn) {
        setIsLoggedIn(true);
        setActiveSession("magento");
        const customerData = customerOperations.getLoggedInCustomerDetails();
        if (!customerData) {
          const details = await getCustomerDetails({ token: session.token });
          setCustomerDetails(details);
          sessionStorage.setItem(CUSTOMER_DETAILS_KEY, details);
        } else {
          setCustomerDetails(customerData);
        }

        const response = await getCpqLogin();
        if (!response.error) {
          setCpqSessionStatus(true);
        } else {
          setCpqSessionStatus(false);
        }
      } else {
        setIsLoggedIn(false);
        setActiveSession(null);

      }
            setIsLoading(false);
    };
    syncSessionWithAuth();
  }, [
    salesforceSession,
    status,
    getCustomerDetails,
    session.token,
    session.isLoggedIn,
    getCpqLogin,
    // isLoggedIn,
    salesforceSession?.access_token
  ]);

  const value = useMemo(
    () => ({
      isLoggedIn,
            isLoading,
      customerDetails,
      setCustomerDetails,
      setIsLoggedIn,
      cpqSessionStatus,
      setCpqSessionStatus,
      activeSession,
    }),
    [isLoggedIn, isLoading, customerDetails, cpqSessionStatus, activeSession]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuthContext = () => useContext(AuthContext);
